import React, { useRef } from 'react';
import { useIsMobile } from '@amzn/storm-ui';
import { PAGE_LAYOUT_TYPE } from 'src/app/slice';
import { BreadcrumbTruncationProvider } from './components/BreadcrumbIA2/BreadcrumbTruncationContext';
import { usePopoverResizeObserver } from 'src/commons/utils/hooks';
import {
  useIsFeatureEnabled,
  useLayoutResizeObserver,
  usePageHeaderLayout,
} from 'src/commons/hooks';
import { ImageProps } from 'src/commons/utils/types';
import { ConnectedConsoleLevelBannerWithAces } from './components/Banners/Common/BannerWithAces';
import { ConnectedPageHeader } from './components/PageHeader/PageHeader';
import SkipNavCard from './components/SkipNavCard/SkipNavCard';
import {
  GlobalStyle,
  HeaderContainer,
  MarketplaceSwitcherWrapper,
  MenuShiftWrapper,
  RegionSwitcherWrapper,
} from './styles';
import { ConsoleHeaderContent, ToastBannerWrapper } from './UtilityComponents';
import MarketplaceSwitcher from './components/MarketplaceSwitcher/MarketplaceSwitcher';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { RegionSwitcherProvider } from './components/RegionSwitcher/RegionSwitcherProvider';
import { AccountDropdownProvider } from 'src/features/header/components/AccountDropdown/AccountDropdownProvider';
import { WeblabedFeatures } from 'src/commons/constants';
import RegionSwitcher from 'src/features/header/components/RegionSwitcher/RegionSwitcher';

export const DesktopHeader: React.VFC<{
  shouldRenderMenu: boolean;
  pageLayoutType: PAGE_LAYOUT_TYPE;
  logo: ImageProps;
  internalLogo?: ImageProps;
}> = ({ shouldRenderMenu, pageLayoutType, logo, internalLogo }) => {
  const chromeConfig = useChromeConfigContext();
  const { config: internalChromeConfig } = useInternalChromeConfigContext();
  const { isInterstitialPage, isFromRodeo } = internalChromeConfig;
  const isMobile = useIsMobile();
  const isRegionSwitcherWeblabEnabled = useIsFeatureEnabled(
    'prod'.toLowerCase() == 'gamma'
      ? WeblabedFeatures.ADSP_GLOBAL_BUYING_REGION_SWITCHER_GAMMA
      : WeblabedFeatures.ADSP_GLOBAL_BUYING_REGION_SWITCHER,
  );
  const consoleHeaderSectionRef = useRef<HTMLDivElement>(null);
  const pageHeaderWrapperRef = useRef<HTMLDivElement>(null);

  const marketplaceSwitcher = chromeConfig?.marketplaceSwitcher;
  const hasMarketplaceSwitcher =
    (marketplaceSwitcher === undefined ||
      marketplaceSwitcher?.useMarketplaceSwitcher) &&
    pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS &&
    pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS_NO_HEADER;

  const hasRegionSwitcher = chromeConfig?.regionSwitcherEnabled;

  usePageHeaderLayout(pageHeaderWrapperRef);

  useLayoutResizeObserver({
    consoleHeaderSectionRef,
  });

  usePopoverResizeObserver({ popoverResizeRef: consoleHeaderSectionRef });

  return (
    <>
      <GlobalStyle
        shouldShiftAppWrapper={shouldRenderMenu}
        isFromRodeo={isFromRodeo}
        isInterstitialPage={isInterstitialPage}
        isMobile={isMobile}
      />
      <SkipNavCard />
      <HeaderContainer
        id="console-header-section"
        ref={consoleHeaderSectionRef}
      >
        <ConnectedConsoleLevelBannerWithAces />
        <BreadcrumbTruncationProvider>
          <ConsoleHeaderContent
            logo={logo}
            internalLogo={internalLogo}
            pageLayoutType={pageLayoutType}
            isMobile={false}
          />
        </BreadcrumbTruncationProvider>
      </HeaderContainer>
      <MenuShiftWrapper
        ref={pageHeaderWrapperRef}
        shouldShiftAppWrapper={shouldRenderMenu}
      >
        {hasMarketplaceSwitcher && (
          <MarketplaceSwitcherWrapper isMobile={false} isFromTrim={false}>
            <MarketplaceSwitcher
              pathOverride={marketplaceSwitcher?.marketplacePath}
            />
          </MarketplaceSwitcherWrapper>
        )}
        {hasRegionSwitcher && isRegionSwitcherWeblabEnabled && (
          <AccountDropdownProvider>
            <RegionSwitcherProvider>
              <RegionSwitcherWrapper>
                <RegionSwitcher />
              </RegionSwitcherWrapper>
            </RegionSwitcherProvider>
          </AccountDropdownProvider>
        )}
        <ConnectedPageHeader />
      </MenuShiftWrapper>
      <ToastBannerWrapper isMobile={false} />
    </>
  );
};
